import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { useUIStore } from '../../stores/uiStore';
import { Download, FileText, Table, Loader2 } from 'lucide-react';
import { useContractStore } from '../../stores/contractStore';
import { toast } from 'react-hot-toast';

interface ExportOptions {
  format: 'pdf' | 'csv';
  dateRange: string;
  includeDetails: boolean;
  sections: string[];
}

const initialOptions: ExportOptions = {
  format: 'csv',
  dateRange: '30',
  includeDetails: true,
  sections: ['contracts', 'proposals', 'compliance'],
};

export function ExportReport() {
  const { modals, closeModal, loading, setLoading } = useUIStore();
  const { exportContracts } = useContractStore();
  const [options, setOptions] = useState<ExportOptions>(initialOptions);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setLoading('export', true);
    setError(null);
    
    try {
      await exportContracts(options.format, options.dateRange);
      closeModal('exportReport');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export report';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading('export', false);
    }
  };

  return (
    <Modal
      isOpen={modals.exportReport}
      onClose={() => closeModal('exportReport')}
      title="Export Report"
    >
      <div className="space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Export Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">Format</label>
          <div className="mt-2 grid grid-cols-2 gap-4">
            <button
              onClick={() => setOptions({ ...options, format: 'pdf' })}
              className={`flex items-center justify-center space-x-2 rounded-lg border p-4 ${
                options.format === 'pdf'
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
            >
              <FileText className="h-5 w-5" />
              <span>PDF</span>
            </button>
            <button
              onClick={() => setOptions({ ...options, format: 'csv' })}
              className={`flex items-center justify-center space-x-2 rounded-lg border p-4 ${
                options.format === 'csv'
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
            >
              <Table className="h-5 w-5" />
              <span>CSV</span>
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="dateRange" className="block text-sm font-medium text-gray-700">
            Date Range
          </label>
          <select
            id="dateRange"
            value={options.dateRange}
            onChange={(e) => setOptions({ ...options, dateRange: e.target.value })}
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Include Sections</label>
          <div className="mt-2 space-y-2">
            {['contracts', 'proposals', 'compliance'].map((section) => (
              <label key={section} className="flex items-center">
                <input
                  type="checkbox"
                  checked={options.sections.includes(section)}
                  onChange={(e) => {
                    const newSections = e.target.checked
                      ? [...options.sections, section]
                      : options.sections.filter((s) => s !== section);
                    setOptions({ ...options, sections: newSections });
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm text-gray-600">
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={options.includeDetails}
              onChange={(e) =>
                setOptions({ ...options, includeDetails: e.target.checked })
              }
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="ml-2 text-sm text-gray-600">Include detailed information</span>
          </label>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => closeModal('exportReport')}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={loading.export}
            className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading.export ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {loading.export ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>
    </Modal>
  );
}